import { useEffect, useState } from "react";
import {
  Cloud, Database, HardDrive, Activity, Loader2, RefreshCw,
  AlertTriangle, CheckCircle2, PlayCircle, Terminal,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";

/**
 * 2026-10-11 · Backup & integrity dashboard — redesigned to match the clean
 * two-column reference layout the user shared (CONFIGURATION + BACKUP STATUS
 * side-by-side, then SNAPSHOT HISTORY underneath). Admin-only entry point.
 */
export default function BackupHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState("");

  const refresh = async () => {
    try {
      const r = await api.get("/backup/status");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "加载失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, []);

  const trigger = async (kind) => {
    setActioning(kind);
    try {
      await api.post(`/backup/${kind}/run`);
      toast.success("已触发，后台运行中…");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "触发失败");
    } finally {
      setActioning("");
    }
  };

  if (loading && !data) {
    return (
      <div className="p-10 text-center text-muted-foreground" data-testid="backup-loading">
        <Loader2 className="animate-spin inline-block" /> 加载中…
      </div>
    );
  }
  if (!data) return null;

  const cfg = data.config || {};
  const s = data.state || {};
  const bucketReady = cfg.gcs_configured;
  const gcs = s.gcs_backup || {};
  const mongo = s.mongo_backup || {};
  const integrity = s.integrity || {};
  const runs = data.recent_runs || [];
  const health = data.recent_health || [];

  // "Last backup" summary — prefer the most recent GCS run (R2 mirror) and
  // fall back to the mongo run if GCS has never completed.
  const lastRun = runs.find((r) => r.kind === "gcs") || runs.find((r) => r.kind === "mongo");
  const lastRunDate = lastRun?.ts ? new Date(lastRun.ts) : null;
  const lastSucceeded = lastRun && !lastRun.error;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-10 py-10 space-y-10" data-testid="backup-health-page">
      {/* Page header -------------------------------------------------------- */}
      <header>
        <p className="text-xs font-semibold tracking-[0.2em] text-brand uppercase mb-2">
          System Administration
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-3">
          备份与健康
        </h1>
        <p className="text-sm text-muted-foreground max-w-3xl leading-relaxed">
          每天凌晨自动把完整的 MongoDB 数据库和所有 Cloudflare R2 对象备份到 Google Cloud Storage，
          超期快照会自动清理。另有每小时完整性探针，命中率低于阈值时自动告警邮件。
        </p>
      </header>

      {!bucketReady && (
        <div
          className="rounded-xl border border-amber-300 bg-amber-50 text-amber-900 p-4 flex gap-3 items-start"
          data-testid="backup-not-configured"
        >
          <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          <div className="text-sm leading-relaxed">
            <strong>GCS 跨厂备份未配置。</strong>到 GCP Console 新建 bucket 和 HMAC key，
            然后在 Emergent → Re-publish → Secrets 加 4 个变量：
            <code className="font-mono mx-0.5 px-1 py-0.5 rounded bg-amber-100">GCS_BUCKET</code>、
            <code className="font-mono mx-0.5 px-1 py-0.5 rounded bg-amber-100">GCS_ACCESS_KEY</code>、
            <code className="font-mono mx-0.5 px-1 py-0.5 rounded bg-amber-100">GCS_SECRET_KEY</code>、
            <code className="font-mono mx-0.5 px-1 py-0.5 rounded bg-amber-100">BACKUP_ALERT_EMAIL</code>，再重新发布。
          </div>
        </div>
      )}

      {/* Top row: Configuration (left) + Backup Status (right) -------------- */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Configuration card ---------------------------------------------- */}
        <section
          className="lg:col-span-2 rounded-xl border border-border bg-white p-6 shadow-sm"
          data-testid="config-card"
        >
          <h2 className="text-xs font-semibold tracking-[0.15em] uppercase text-muted-foreground flex items-center gap-2 mb-5">
            <Cloud size={15} className="text-brand" /> Configuration
          </h2>
          <dl className="divide-y divide-border text-sm">
            <ConfigRow label="GCS Bucket" value={cfg.gcs_bucket || "—"} mono />
            <ConfigRow label="GCP 项目" value={bucketReady ? "✓ 已连接" : "⚠ 未配置"} mono />
            <ConfigRow label="保留期" value={`${cfg.retention_days || 30} 天`} />
            <ConfigRow label="自动任务" value="每天 02:00 UTC · R2 镜像" />
            <ConfigRow label="Mongo 备份" value="每天 02:30 UTC" />
            <ConfigRow label="完整性探针" value={`每小时 · 抽样 ${cfg.probe_sample_size || 100}`} />
            <ConfigRow label="告警邮箱" value={cfg.alert_email || "—"} mono />
          </dl>
        </section>

        {/* Backup Status card ---------------------------------------------- */}
        <section
          className="lg:col-span-3 rounded-xl border border-border bg-white p-6 shadow-sm"
          data-testid="status-card"
        >
          <div className="flex items-start justify-between gap-3 mb-5">
            <h2 className="text-xs font-semibold tracking-[0.15em] uppercase text-muted-foreground flex items-center gap-2">
              <Database size={15} className="text-brand" /> Backup Status
            </h2>
            <div className="flex items-center gap-2">
              <Button
                size="sm" variant="outline" className="rounded-md h-9 w-9 p-0"
                onClick={refresh} data-testid="refresh-btn" title="刷新"
              >
                <RefreshCw size={14} />
              </Button>
              <Button
                size="sm"
                className="rounded-md h-9 bg-brand text-brand-foreground hover:bg-brand/90 px-4"
                disabled={!bucketReady || actioning === "gcs"}
                onClick={() => trigger("gcs")}
                data-testid="run-gcs-btn"
              >
                {actioning === "gcs"
                  ? <Loader2 size={14} className="mr-1.5 animate-spin" />
                  : <Cloud size={14} className="mr-1.5" />}
                立即备份
              </Button>
            </div>
          </div>

          {/* Current state ------------------------------------------------- */}
          <StatusHero
            running={gcs.state === "running"}
            runningState={gcs}
            lastRun={lastRun}
            lastRunDate={lastRunDate}
            lastSucceeded={lastSucceeded}
          />

          {/* Live progress bar during an in-flight run --------------------- */}
          {gcs.state === "running" && <GcsProgress state={gcs} />}

          {/* Secondary action: Mongo-only backup --------------------------- */}
          <div className="mt-5 pt-5 border-t border-border flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Database size={12} /> MongoDB 独立备份：{mongo.collections || 0} 集合 · {(mongo.docs || 0).toLocaleString()} 文档
                {mongo.size_bytes ? ` · ${formatBytes(mongo.size_bytes)}` : ""}
              </span>
            </div>
            <Button
              size="sm" variant="outline" className="rounded-md h-8"
              disabled={!bucketReady || actioning === "mongo"}
              onClick={() => trigger("mongo")} data-testid="run-mongo-btn"
            >
              {actioning === "mongo"
                ? <Loader2 size={12} className="mr-1 animate-spin" />
                : <PlayCircle size={12} className="mr-1" />}
              只备份 Mongo
            </Button>
          </div>
        </section>
      </div>

      {/* Integrity probe ---------------------------------------------------- */}
      <section
        className="rounded-xl border border-border bg-white p-6 shadow-sm"
        data-testid="integrity-card"
      >
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div>
            <h2 className="text-xs font-semibold tracking-[0.15em] uppercase text-muted-foreground flex items-center gap-2 mb-1">
              <Activity size={15} className="text-brand" /> Integrity Probe
            </h2>
            <p className="text-xs text-muted-foreground">
              每小时随机抽 {cfg.probe_sample_size || 100} 条 Mongo 图片 URL 探测 R2，
              命中率低于 {Math.round((cfg.health_threshold || 0.95) * 100)}% 自动发邮件告警。
            </p>
          </div>
          <Button
            size="sm" variant="outline" className="rounded-md h-9"
            disabled={actioning === "probe"} onClick={() => trigger("probe")}
            data-testid="run-probe-btn"
          >
            {actioning === "probe"
              ? <Loader2 size={14} className="mr-1.5 animate-spin" />
              : <PlayCircle size={14} className="mr-1.5" />}
            立即探测
          </Button>
        </div>
        <IntegritySummary state={integrity} />
        <HealthStrip metrics={health} threshold={cfg.health_threshold} />
      </section>

      {/* Snapshot history --------------------------------------------------- */}
      <section data-testid="snapshot-history">
        <h2 className="text-xs font-semibold tracking-[0.15em] uppercase text-muted-foreground flex items-center gap-2 mb-4">
          <HardDrive size={15} className="text-brand" /> Snapshot History
        </h2>
        <div className="rounded-xl border border-border bg-white overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] font-semibold tracking-[0.1em] uppercase text-muted-foreground border-b border-border">
                <th className="text-left px-5 py-3 w-44">时间</th>
                <th className="text-left px-5 py-3 w-24">类型</th>
                <th className="text-right px-5 py-3">数量 / 结果</th>
                <th className="text-right px-5 py-3 w-32">大小</th>
                <th className="text-left px-5 py-3">错误</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-muted-foreground">
                    暂无备份记录
                  </td>
                </tr>
              )}
              {runs.map((r) => (
                <tr key={r._id} className="border-b border-border last:border-b-0 hover:bg-secondary/20">
                  <td className="px-5 py-3 font-mono text-xs whitespace-nowrap">
                    {formatRunTs(r.ts)}
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-block text-[10px] font-mono px-2 py-0.5 rounded-full ${
                      r.kind === "gcs"
                        ? "bg-blue-50 text-blue-700"
                        : r.kind === "mongo"
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-secondary text-muted-foreground"
                    }`}>
                      {r.kind}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right text-sm font-mono">
                    {r.kind === "gcs" && (
                      <>复制 {(r.copied || 0).toLocaleString()} · 跳过 {(r.skipped || 0).toLocaleString()}</>
                    )}
                    {r.kind === "mongo" && (
                      <>{r.collections || 0} 集合 · {(r.docs || 0).toLocaleString()} 文档</>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right text-sm font-mono text-muted-foreground">
                    {r.kind === "mongo"
                      ? formatBytes(r.size_bytes || 0)
                      : r.kind === "gcs" && r.size_bytes
                        ? formatBytes(r.size_bytes)
                        : "—"}
                  </td>
                  <td className="px-5 py-3 text-xs text-red-600 max-w-xs truncate">{r.error || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Restore cheatsheet ------------------------------------------------- */}
      <section
        className="rounded-xl border border-dashed border-border bg-secondary/20 p-5"
        data-testid="restore-cheatsheet"
      >
        <h2 className="text-xs font-semibold tracking-[0.15em] uppercase text-muted-foreground flex items-center gap-2 mb-3">
          <Terminal size={14} className="text-brand" /> 恢复流程（手动）
        </h2>
        <p className="text-xs text-muted-foreground mb-3">
          UI 不提供恢复按钮以避免误操作。进入生产 shell 后执行脚本：
        </p>
        <pre className="bg-neutral-950 text-neutral-100 rounded-lg p-4 overflow-x-auto text-xs leading-relaxed">
{`# 查看可用的 Mongo 快照
python3 /app/backend/scripts/restore_mongo_backup.py --list

# 把特定 R2 keys 从 GCS 备份桶恢复到主桶
python3 /app/backend/scripts/restore_r2_backup.py --prefix survey-fm-

# Mongo 恢复到带日期后缀的 DB（安全：先对比再切换）
python3 /app/backend/scripts/restore_mongo_backup.py --date 2026-10-10 \\
    --target-db quotesys_restore_20261010`}
        </pre>
      </section>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

function ConfigRow({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between py-3 first:pt-0 last:pb-0 gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={`text-right text-foreground font-semibold ${mono ? "font-mono text-xs" : ""} truncate`}>
        {value}
      </dd>
    </div>
  );
}

function StatusHero({ running, runningState, lastRun, lastRunDate, lastSucceeded }) {
  if (running) {
    return (
      <div className="flex items-center gap-3 mb-2" data-testid="status-running">
        <Loader2 size={18} className="animate-spin text-brand shrink-0" />
        <div>
          <div className="font-semibold text-foreground">备份进行中</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            启动于 {runningState.started_at ? new Date(runningState.started_at).toLocaleString() : "—"}
          </div>
        </div>
      </div>
    );
  }
  if (!lastRun) {
    return (
      <div className="flex items-center gap-3 text-muted-foreground" data-testid="status-never">
        <Cloud size={18} className="shrink-0" />
        <div className="text-sm">尚未执行过备份</div>
      </div>
    );
  }
  const icon = lastSucceeded
    ? <CheckCircle2 size={18} className="text-emerald-600 shrink-0" />
    : <AlertTriangle size={18} className="text-red-600 shrink-0" />;
  const text = lastSucceeded ? "上次备份成功" : "上次备份失败";
  return (
    <div data-testid="status-done">
      <div className="flex items-center gap-3 mb-5">
        {icon}
        <div className="font-semibold text-foreground">{text}</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricTile
          label="日期"
          value={lastRunDate ? lastRunDate.toISOString().slice(0, 10) : "—"}
          sub={lastRunDate ? lastRunDate.toLocaleString() : ""}
        />
        {lastRun.kind === "gcs" ? (
          <>
            <MetricTile label="已复制" value={(lastRun.copied || 0).toLocaleString()} />
            <MetricTile label="已有（跳过）" value={(lastRun.skipped || 0).toLocaleString()} />
            <MetricTile
              label="错误"
              value={lastRun.errors || 0}
              danger={!!lastRun.errors}
            />
          </>
        ) : (
          <>
            <MetricTile label="集合数" value={lastRun.collections || 0} />
            <MetricTile label="文档数" value={(lastRun.docs || 0).toLocaleString()} />
            <MetricTile label="压缩大小" value={formatBytes(lastRun.size_bytes || 0)} />
          </>
        )}
      </div>
    </div>
  );
}

function MetricTile({ label, value, sub, danger = false }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
        {label}
      </div>
      <div className={`font-mono text-lg font-semibold ${danger ? "text-red-600" : "text-foreground"}`}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

function GcsProgress({ state }) {
  const total = state.total || 0;
  const done = (state.copied || 0) + (state.skipped || 0) + (state.errors || 0);
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 1000) / 10) : 0;
  const elapsedMs = state.started_at ? Date.now() - new Date(state.started_at).getTime() : 0;
  const rate = elapsedMs > 0 ? (state.copied || 0) / (elapsedMs / 1000) : 0;
  const remaining = total - done;
  const etaSec = rate > 0 ? Math.round(remaining / rate) : null;
  const etaText = etaSec === null
    ? "估算中…"
    : etaSec > 3600
      ? `约 ${Math.round(etaSec / 360) / 10} 小时`
      : etaSec > 60
        ? `约 ${Math.round(etaSec / 60)} 分钟`
        : `约 ${etaSec} 秒`;
  return (
    <div className="mt-4" data-testid="gcs-progress">
      <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
        <span>进度 {pct}% · {done.toLocaleString()} / {total.toLocaleString()}</span>
        <span>{rate.toFixed(1)} 个/秒 · 剩余 {etaText}</span>
      </div>
      <div className="h-2 bg-secondary rounded-full overflow-hidden">
        <div className="h-full bg-brand transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function IntegritySummary({ state }) {
  if (!state) return null;
  const rate = state.hit_rate;
  const pct = rate !== null && rate !== undefined ? Math.round(rate * 10000) / 100 : null;
  const color = pct === null
    ? "text-muted-foreground"
    : pct >= 95
      ? "text-emerald-600"
      : pct >= 90
        ? "text-amber-600"
        : "text-red-600";
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
      <MetricTile
        label="上次运行"
        value={state.last_run ? new Date(state.last_run).toLocaleTimeString() : "—"}
        sub={state.last_run ? new Date(state.last_run).toISOString().slice(0, 10) : ""}
      />
      <MetricTile label="抽样" value={state.sampled || 0} />
      <MetricTile label="命中" value={state.hits || 0} />
      <div>
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">命中率</div>
        <div className={`font-mono text-lg font-semibold ${color}`}>
          {pct !== null ? pct + "%" : "—"}
        </div>
        {state.alerted && <div className="text-[10px] text-red-600 mt-0.5">⚠ 已发送告警</div>}
      </div>
    </div>
  );
}

function HealthStrip({ metrics, threshold = 0.95 }) {
  if (!metrics?.length) {
    return (
      <p className="text-xs text-muted-foreground py-4 italic border-t border-border">
        暂无探针历史数据，运行一次后展示 72 小时走势。
      </p>
    );
  }
  const sorted = [...metrics].reverse();
  return (
    <div className="pt-3 border-t border-border">
      <div className="flex items-end gap-0.5 h-20" data-testid="health-strip">
        {sorted.map((m) => {
          const pct = m.hit_rate ?? 0;
          const h = Math.max(2, Math.round(pct * 80));
          const good = pct >= threshold;
          return (
            <div
              key={m._id}
              title={`${m.ts} · ${(pct * 100).toFixed(1)}%`}
              className={`flex-1 ${good ? "bg-emerald-500" : "bg-red-500"} rounded-sm transition-colors`}
              style={{ height: `${h}px` }}
            />
          );
        })}
      </div>
      <p className="mt-2 text-[10px] text-muted-foreground">
        最近 72 小时 · 阈值 {Math.round(threshold * 100)}%
      </p>
    </div>
  );
}

function formatRunTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toISOString().slice(0, 19).replace("T", " ");
  } catch {
    return iso;
  }
}

function formatBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
